import React from 'react';
import { View, Text } from 'react-native';
import AnimatedPressable from '@/components/AnimatedPressable';
import { Scale, Star, X } from 'lucide-react-native';
import Animated, { FadeInUp } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';

interface ComparisonBarProps {
  comparisonCount: number;
  onCompare: () => void;
  onClear: () => void;
}

export function ComparisonBar({ comparisonCount, onCompare, onClear }: ComparisonBarProps) {
  const colors = useThemeColors();

  return (
    <Animated.View
      entering={FadeInUp.duration(300)}
      className="absolute bottom-0 left-0 right-0 px-5 py-4"
      style={{
        backgroundColor: colors.surface.primary,
        borderTopWidth: 1,
        borderTopColor: colors.border.light,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -4 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
      }}
    >
      <View className="flex-row items-center justify-between">
        <View>
          <Text style={{ color: colors.text.primary }} className="font-medium">
            {comparisonCount} selected for comparison
          </Text>
          <Text style={{ color: colors.text.tertiary }} className="text-xs">
            Select 2-4 startups
          </Text>
        </View>
        <View className="flex-row items-center gap-3">
          <AnimatedPressable onPress={onClear} accessibilityRole="button" accessibilityLabel="Clear comparison selection" className="px-4 py-2">
            <Text style={{ color: colors.text.secondary }} className="font-medium">Clear</Text>
          </AnimatedPressable>
          <AnimatedPressable
            onPress={onCompare}
            disabled={comparisonCount < 2}
            accessibilityRole="button"
            accessibilityLabel={`Compare ${comparisonCount} selected startups`}
            accessibilityState={{ disabled: comparisonCount < 2 }}
            className="px-5 py-2.5 rounded-xl flex-row items-center gap-2"
            style={{
              backgroundColor: comparisonCount >= 2 ? colors.primary[600] : colors.light[300],
            }}
          >
            <Scale size={18} color={comparisonCount >= 2 ? 'white' : colors.text.tertiary} />
            <Text
              className="font-medium"
              style={{ color: comparisonCount >= 2 ? 'white' : colors.text.tertiary }}
            >
              Compare
            </Text>
          </AnimatedPressable>
        </View>
      </View>
    </Animated.View>
  );
}

interface BulkActionsBarProps {
  selectedCount: number;
  onBulkInterested: () => void;
  onBulkPass: () => void;
}

export function BulkActionsBar({ selectedCount, onBulkInterested, onBulkPass }: BulkActionsBarProps) {
  const colors = useThemeColors();

  return (
    <Animated.View
      entering={FadeInUp.duration(300)}
      className="absolute bottom-0 left-0 right-0 px-5 py-4"
      style={{
        backgroundColor: colors.primary[600],
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -4 },
        shadowOpacity: 0.15,
        shadowRadius: 12,
      }}
    >
      <View className="flex-row items-center justify-between">
        <Text className="text-white font-medium">
          {selectedCount} deal{selectedCount > 1 ? 's' : ''} selected
        </Text>
        <View className="flex-row items-center gap-3">
          <AnimatedPressable
            onPress={onBulkInterested}
            accessibilityRole="button"
            accessibilityLabel={`Mark ${selectedCount} selected deals as interested`}
            className="px-4 py-2 rounded-lg flex-row items-center gap-2"
            style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
          >
            <Star size={16} color="white" />
            <Text className="text-white font-medium">Interested</Text>
          </AnimatedPressable>
          <AnimatedPressable
            onPress={onBulkPass}
            accessibilityRole="button"
            accessibilityLabel={`Pass on ${selectedCount} selected deals`}
            className="px-4 py-2 rounded-lg flex-row items-center gap-2"
            style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
          >
            <X size={16} color="white" />
            <Text className="text-white font-medium">Pass All</Text>
          </AnimatedPressable>
        </View>
      </View>
    </Animated.View>
  );
}
